import React, { useState } from 'react';
import { X, Trophy, Lock, Sparkles } from 'lucide-react';
import MediaCaptureMenu from '../MediaCaptureMenu';
import PasteUrlInput from '../PasteUrlInput';
import AttachmentTile from '../AttachmentTile';
import { fileToPayload, urlToPayload } from '../../lib/mediaCapture';

// G6: free Egnite tier caps photo/video attachments per session.
// Paid Enferno is unlimited. 3 is chosen to cover the common "whiteboard +
// 2 reference photos" case without making the basic log feel crippled.
const FREE_ATTACHMENT_CAP = 3;

/**
 * LogSessionDialog — the generic session log dialog used for every art
 * EXCEPT BJJ. BJJ uses BJJLogDialog (richer journal flow). This is the
 * historical inline dialog from Training.jsx, extracted as-is.
 * Follow-up: media flow rewritten to filename-only + inline
 * playback from device storage. No dataUrl sent to cloud.
 *
 * Schema written: { date, tz_offset_minutes, focus, duration, notes, attachments }
 *
 * Accepts `isPaid` + `onUpsell` so the generic dialog can enforce
 * the free-tier limits (3 attachments cap, no YouTube/Vimeo link input)
 * without leaking tier logic into every art-specific dialog.
 */
const LogSessionDialog = ({ art, onClose, onSave, isPaid = true, onUpsell = () => {} }) => {
  const todayLocal = (() => {
    const d = new Date();
    const tzOff = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOff).toISOString().slice(0, 10);
  })();
  const minLocal = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    const tzOff = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOff).toISOString().slice(0, 10);
  })();
  const [form, setForm] = useState({ date: todayLocal, focus: art.focusAreas[0], duration: '60', notes: '', attachments: [] });
  const [error, setError] = useState('');

  const addAttachment = (payload) => {
    setError('');
    if (!payload) return;
    if (payload instanceof Blob && !('kind' in payload)) {
      fileToPayload(payload).then((p) => {
        setForm((prev) => ({ ...prev, attachments: [...prev.attachments, p] }));
      });
      return;
    }
    setForm((prev) => ({ ...prev, attachments: [...prev.attachments, payload] }));
  };
  const addExternalUrl = (url) => {
    setForm((prev) => ({ ...prev, attachments: [...prev.attachments, urlToPayload(url)] }));
  };

  const removeAtt = (i) => setForm({ ...form, attachments: form.attachments.filter((_, idx) => idx !== i) });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-1">Log session · {art.shortName}</div>
            <div className="font-display text-3xl tracking-wide leading-none">New training session</div>
          </div>
          <button onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Date</label>
            <input
              type="date"
              value={form.date}
              max={todayLocal}
              min={minLocal}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
              data-testid="session-date"
            />
            <p className="text-xs text-enf-text-muted mt-1">Defaults to today. Can log up to 14 days back.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Focus</label>
            <div className="flex flex-wrap gap-1.5">
              {art.focusAreas.map((f) => (
                <button key={f} type="button" onClick={() => setForm({ ...form, focus: f })}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                    form.focus === f ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                  }`}>{f}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Duration (min)</label>
            <input type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Techniques drilled, rolls, takeaways, what to work on next..."
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg min-h-[120px] resize-none text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Attachments ({form.attachments.length})</label>
            <div className="space-y-2">
              {(isPaid || form.attachments.length < FREE_ATTACHMENT_CAP) ? (
                <MediaCaptureMenu
                  onCapture={(payload) => addAttachment(payload)}
                  accept="both"
                  multiple={false}
                  label="Add photo or video"
                  testId="session-attachment-add"
                  recordingEtiquette
                  className="w-full p-3 border border-dashed border-enf-surface-border hover:border-enf-role-info rounded-md text-sm text-enf-text-secondary hover:text-enf-role-info transition-colors flex items-center justify-center gap-2"
                />
              ) : (
                <button
                  type="button"
                  onClick={onUpsell}
                  data-testid="session-attachment-cap-upsell"
                  className="w-full p-3 border border-dashed border-enf-accent-fg/40 hover:border-enf-accent-fg rounded-md text-sm text-enf-accent-fg hover:text-enf-text-primary transition-colors flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Add more — Enferno (Egnite caps at {FREE_ATTACHMENT_CAP})
                </button>
              )}
              {isPaid ? (
                <PasteUrlInput onAdd={addExternalUrl} testId="session-paste-url" />
              ) : (
                <button
                  type="button"
                  onClick={onUpsell}
                  data-testid="session-paste-url-upsell"
                  className="w-full p-2.5 border border-dashed border-enf-surface-border hover:border-enf-accent-fg/60 rounded-md text-xs text-enf-text-muted hover:text-enf-accent-fg transition-colors flex items-center justify-center gap-2"
                >
                  <Lock className="w-3.5 h-3.5" />
                  Paste a YouTube or Vimeo link — Enferno feature
                </button>
              )}
            </div>
            {error && <div className="text-xs text-enf-role-warning mt-2">{error}</div>}
            {form.attachments.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {form.attachments.map((a, i) => (
                  <AttachmentTile key={i} att={a} onRemove={() => removeAtt(i)} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors">
            Cancel
          </button>
          <button onClick={() => {
            const isToday = form.date === todayLocal;
            const sessionDate = isToday
              ? new Date().toISOString()
              : new Date(`${form.date}T12:00:00`).toISOString();
            onSave({
              date: sessionDate,
              tz_offset_minutes: new Date().getTimezoneOffset(),
              focus: form.focus,
              duration: Number(form.duration) || 60,
              notes: form.notes,
              attachments: form.attachments,
            });
          }}
            className="flex-1 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
            <Trophy className="w-4 h-4" /> Save session
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogSessionDialog;
